import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

import { Button } from "@/components/button"
import { CardActionBar } from "@/components/shared/card-action-bar"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  LabeledSelect,
  type LabeledSelectOption,
} from "@/components/ui/labeled-select"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/components/ui/toast"
import {
  DAILY_CONTENT_KIND_VALUES,
  type DailyContentKind,
} from "@/core/daily-content/kinds"
import { refreshProfiles } from "@/hooks/queries/profile.queries"
import { dailyContentApi } from "@/lib/daily-content-api"
import { dailyContentKeys } from "@/lib/query-keys"
import {
  useIsOffline,
  useNewProfileDraft,
  type DailyContentUiStore,
  type ProfileDraft,
} from "@/lib/stores/daily-content-ui-store"

const KIND_OPTIONS: readonly LabeledSelectOption<DailyContentKind>[] =
  DAILY_CONTENT_KIND_VALUES.map((kind) => ({
    value: kind,
    label: kind.charAt(0).toUpperCase() + kind.slice(1),
  }))

const FALLBACK_OPTIONS: readonly LabeledSelectOption<"none" | "preset">[] = [
  { value: "none", label: "None" },
  { value: "preset", label: "Preset" },
]

type CreateProfileCardProps = {
  store: DailyContentUiStore
}

function useCanShowSuccessToast() {
  const router = useRouter()
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => {
    const pathname = router.state.location.pathname
    return (
      isMounted.current &&
      (pathname === "/daily-content" || pathname === "/daily-content/")
    )
  }, [router])
}

export function CreateProfileCard({ store }: CreateProfileCardProps) {
  const queryClient = useQueryClient()
  const canShowSuccessToast = useCanShowSuccessToast()
  const newProfileDraft = useNewProfileDraft(store)
  const isOffline = useIsOffline(store)

  const createProfileMutation = useMutation({
    mutationFn: () =>
      dailyContentApi.createProfile({
        name: newProfileDraft.name,
        kind: newProfileDraft.kind,
        targetChannelId: newProfileDraft.targetChannelId,
        scheduleValue: newProfileDraft.scheduleValue,
        fallbackMode: newProfileDraft.fallbackMode,
        pingRoleId: newProfileDraft.pingRoleId || null,
        pinMessage: newProfileDraft.pinMessage,
        createThread: newProfileDraft.createThread,
      }),
    onSuccess: async () => {
      store.getState().setPageError(null)
      if (canShowSuccessToast()) {
        toast.success("Profile created")
      }
      store.getState().resetNewProfileDraft()
      await refreshProfiles(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setPageError(
          error instanceof Error ? error.message : "Failed to create profile",
        )
    },
  })

  const updateField = useCallback(
    <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
      store.getState().updateNewProfileDraftField(key, value)
    },
    [store],
  )

  return (
    <Card>
      <CardHeader>
        <CardHeading icon={<Plus className="size-4" />} className="inline-flex">
          Create Profile
        </CardHeading>
        <CardDescription>
          Cron is evaluated in UTC for scheduler v1.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input
              value={newProfileDraft.name}
              onChange={(event) => {
                updateField("name", event.target.value)
              }}
              placeholder="Daily Question"
            />
          </div>
          <LabeledSelect
            label="Kind"
            value={newProfileDraft.kind}
            options={KIND_OPTIONS}
            onValueChange={(kind) => {
              updateField("kind", kind)
            }}
          />
          <div className="grid gap-1.5">
            <Label>Target Channel ID</Label>
            <Input
              value={newProfileDraft.targetChannelId}
              onChange={(event) => {
                updateField("targetChannelId", event.target.value)
              }}
              placeholder="123456789012345678"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Cron (UTC)</Label>
            <Input
              value={newProfileDraft.scheduleValue}
              onChange={(event) => {
                updateField("scheduleValue", event.target.value)
              }}
              placeholder="0 9 * * *"
            />
          </div>
          <LabeledSelect
            label="Fallback Mode"
            value={newProfileDraft.fallbackMode}
            options={FALLBACK_OPTIONS}
            onValueChange={(fallbackMode) => {
              updateField("fallbackMode", fallbackMode)
            }}
          />
          <div className="grid gap-1.5">
            <Label>Ping Role ID (optional)</Label>
            <Input
              value={newProfileDraft.pingRoleId}
              onChange={(event) => {
                updateField("pingRoleId", event.target.value)
              }}
              placeholder="987654321098765432"
            />
          </div>
        </div>

        <div className="inline-card grid gap-3 bg-card/45 p-4 sm:grid-cols-2">
          <div className="inline-flex items-center gap-2 text-sm">
            <Switch
              id="new-profile-pin-message"
              checked={newProfileDraft.pinMessage}
              onCheckedChange={(checked) => {
                updateField("pinMessage", checked)
              }}
            />
            <Label
              htmlFor="new-profile-pin-message"
              className="text-sm font-normal"
            >
              Pin published message
            </Label>
          </div>
          <div className="inline-flex items-center gap-2 text-sm">
            <Switch
              id="new-profile-create-thread"
              checked={newProfileDraft.createThread}
              onCheckedChange={(checked) => {
                updateField("createThread", checked)
              }}
            />
            <Label
              htmlFor="new-profile-create-thread"
              className="text-sm font-normal"
            >
              Create follow-up thread
            </Label>
          </div>
        </div>

        <CardActionBar className="sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
          <Button
            onClick={() => {
              createProfileMutation.mutate()
            }}
            disabled={createProfileMutation.isPending || isOffline}
            className="w-full sm:w-auto"
          >
            <Plus className="size-4" />
            {isOffline ? "Offline" : "Create Profile"}
          </Button>
        </CardActionBar>
      </CardContent>
    </Card>
  )
}
